import * as Sentry from '@sentry/nextjs'
import axios from 'lib/axios'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import Head from 'next/head'
import { Fragment } from 'react'

import { CompetitionsPageContent } from '@/components/Competition'
import { useLanguage } from '@/hooks/Contexts'
import { API_URL } from '@/utils/Consts'

export const Home = ({ data, banners }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { navigation: navStr } = useLanguage()
  return (
    <Fragment>
      <Head>
        <title>{navStr.meta.home_title}</title>
        <meta name='description' content={navStr.meta.home} key='description' />
        <meta name='viewport' content='width=device-width, initial-scale=1' />
      </Head>
      <CompetitionsPageContent dataFetched={data} bannersFetched={banners} heading={navStr.meta.home_heading} />
    </Fragment>
  )
}
export default Home

// Prefetch the homepage's above-the-fold data (competitions + banner) server-side so the
// hero banner and competition grid don't have to wait for a client round-trip before rendering.
export const getServerSideProps: GetServerSideProps = async context => {
  // Without this, getServerSideProps makes Next send no-cache headers and CloudFront stops
  // caching the homepage HTML entirely (it was a long-lived static page before), pushing every
  // page view to origin. The prefetched data is the public catalog — identical for all users —
  // so letting the CDN serve it up to 60s stale only makes it fresher than the old static page.
  context.res.setHeader('Cache-Control', 'public, s-maxage=60, stale-while-revalidate=300')
  const headers = {
    'X-Requested-With': 'XMLHttpRequest',
    'accept': 'application/json',
    'Content-Type': 'application/json',
    'cookie': context.req.headers.cookie || [],
  }
  // On failure return null, NOT [] — [] is truthy, so it would disable the client-side SWR
  // fallback in CompetitionsPageContent/BannerSlider and render an empty page that the CDN
  // could then cache for up to 60s for everyone. null keeps the old client-fetch behavior.
  const [data, banners] = await Promise.all([
    axios
      .get(API_URL.competitionList + '?store_id=1&source=c', { headers, withCredentials: true })
      .then(res => res?.data?.data || [])
      .catch(e => {
        Sentry.captureException(e)
        return null
      }),
    axios
      .get(API_URL.banners, { headers, withCredentials: true })
      .then(res => res?.data?.data || [])
      .catch(e => {
        Sentry.captureException(e)
        return null
      }),
  ])
  return { props: { data, banners } }
}
